import { createFileRoute, Link, Outlet, useLocation } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchApprovedCommunitySubmissions,
  submitCommunityPhoto,
  type CommunitySubmission,
} from "@/lib/community-submissions";
import { SETTING_COMMUNITY_MAX_IMAGES, getSettingInt } from "@/lib/app-settings";
import { getISOWeek, type SelectedItem } from "@/lib/selected-weeks";
import { fetchPicksForWeek, fetchPickedWeeks } from "@/lib/selected-week-picks";
import { PhotoLightbox, type LightboxInfo } from "@/components/PhotoLightbox";
import { themes } from "@/data/photographers";
import { SmartImg } from "@/components/SmartImg";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/selected")({
  head: () => ({
    meta: [
      { title: "Selected, OM Selected" },
      { name: "description", content: "Community Selected, kuratiertes Archiv eingereichter Bilder." },
      { property: "og:title", content: "Selected, OM Selected" },
      { property: "og:description", content: "Community Selected, kuratiertes Archiv eingereichter Bilder." },
      { property: "og:url", content: "https://omselected.art/selected" },
    ],
    links: [{ rel: "canonical", href: "https://omselected.art/selected" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Selected, OM Selected",
          url: "https://omselected.art/selected",
          description: "Wöchentlich kuratierte Auswahl aus Mitglieder-Arbeiten und Community-Einreichungen im OM Selected Magazin.",
          inLanguage: "de-DE",
          isPartOf: {
            "@type": "WebSite",
            name: "OM Selected",
            url: "https://omselected.art",
          },
        }),
      },
    ],
  }),
  component: SelectedPage,
});

function SelectedPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const [curated, setCurated] = useState<SelectedItem[]>([]);
  const [curatedLoading, setCuratedLoading] = useState(true);
  const [community, setCommunity] = useState<CommunitySubmission[]>([]);
  const [communityLoading, setCommunityLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const current = useMemo(() => getISOWeek(new Date()), []);
  const [landscape, setLandscape] = useState<Record<string, boolean>>({});
  const [displayWeek, setDisplayWeek] = useState(current.week);

  const galleryItems = useMemo(() => [
    ...community.map((c) => ({
      key: `c-${c.id}`,
      image: c.image_url,
      caption: c.caption ?? c.submitter_name,
      byline: c.submitter_name + (c.region ? ` · ${c.region}` : ""),
      source: "Community" as const,
      info: {
        image: c.image_url,
        caption: c.caption,
        photographerName: c.submitter_name,
        region: c.region,
        instagramHandle: c.instagram_handle,
        websiteUrl: c.website_url,
      } as LightboxInfo,
    })),
    ...curated.map((w) => ({
      key: `m-${w.id}`,
      image: w.image,
      caption: w.caption,
      byline: w.photographerName,
      source: "Member" as const,
      info: {
        image: w.image,
        caption: w.caption,
        photographerName: w.photographerName,
        photographerId: w.photographerId,
        region: w.region,
        theme: w.theme,
        instagramHandle: w.instagramHandle,
        websiteUrl: w.websiteUrl,
      } as LightboxInfo,
    })),
  ], [community, curated]);

  useEffect(() => {
    let active = true;
    (async () => {
      // Prefer the current ISO week, but fall back to the most recent week
      // that actually has curated picks so the latest selection stays visible.
      let targetYear = current.year;
      let targetWeek = current.week;
      const weeks = await fetchPickedWeeks();
      const hasCurrent = weeks.some((w) => w.year === current.year && w.week === current.week);
      if (!hasCurrent && weeks.length > 0) {
        targetYear = weeks[0].year;
        targetWeek = weeks[0].week;
      }
      const items = await fetchPicksForWeek(targetYear, targetWeek);
      if (!active) return;
      setDisplayWeek(targetWeek);
      setCurated(items);
      setCuratedLoading(false);
    })();
    fetchApprovedCommunitySubmissions(200).then((data) => {
      if (!active) return;
      const inWeek = data.filter((c) => {
        const d = new Date(c.reviewed_at ?? c.created_at);
        const w = getISOWeek(d);
        return w.year === current.year && w.week === current.week;
      });
      setCommunity(inWeek);
      setCommunityLoading(false);
    });
    return () => { active = false; };
  }, [current.year, current.week]);

  if (location.pathname !== "/selected") {
    return <Outlet />;
  }

  return (
    <div>
      <CommunityUploadSection />

      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-20">
          <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
            <div>
              <div className="eyebrow text-muted-foreground">{t("selectedPage.thisWeek")}</div>
              <h2 className="serif text-3xl md:text-4xl mt-3">{t("selectedPage.weekTitle", { week: displayWeek })}</h2>
              <p className="mt-3 text-sm text-muted-foreground max-w-xl">
                {t("selectedPage.weekDesc")}
              </p>
            </div>
            <div className="eyebrow text-muted-foreground">{t("selectedPage.memberCommunity")}</div>
          </div>
          {curatedLoading || communityLoading ? (
            <p className="text-muted-foreground text-sm">{t("common.loading")}</p>
          ) : curated.length === 0 && community.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t("selectedPage.noImages")}</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 [grid-auto-flow:dense]">
              {galleryItems.map((it, i) => (
                <button
                  key={it.key}
                  type="button"
                  onClick={() => setLightboxIndex(i)}
                  className={`block w-full text-left group ${landscape[it.key] ? "col-span-2" : ""}`}
                >
                  <div className="relative">
                    <SmartImg
                      source={it.image}
                      transform={{ width: 1200, resize: "contain" }}
                      srcSetWidths={[400, 600, 900, 1200, 1600]}
                      sizes={
                        landscape[it.key]
                          ? "(min-width: 1024px) 50vw, (min-width: 768px) 66vw, 100vw"
                          : "(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
                      }
                      alt={`${it.caption ? `${it.caption} — ` : ""}selected photography${it.byline ? ` by ${it.byline}` : ""}`}
                      loading={i === 0 ? "eager" : "lazy"}
                      fetchPriority={i === 0 ? "high" : "auto"}
                      decoding="async"
                      onLoad={(e) => {
                        const img = e.currentTarget;
                        const isLandscape = img.naturalWidth > img.naturalHeight * 1.15;
                        setLandscape((prev) =>
                          prev[it.key] === isLandscape ? prev : { ...prev, [it.key]: isLandscape }
                        );
                      }}
                      className="w-full h-auto object-contain cursor-zoom-in"
                    />
                    <span className="absolute top-2 left-2 eyebrow bg-background/70 px-1.5 py-0.5 text-[9px] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      {it.source}
                    </span>
                  </div>
                  <div className="mt-3">
                    <div className="serif text-base md:text-lg">{it.caption}</div>
                    <div className="eyebrow text-muted-foreground mt-1">{it.byline}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="border-b border-border bg-muted/10">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-12 flex flex-wrap items-baseline justify-between gap-4">
          <div>
            <div className="eyebrow text-muted-foreground">{t("selectedPage.archiveEyebrow")}</div>
            <h2 className="serif text-2xl md:text-3xl mt-3">{t("selectedPage.archiveTitle")}</h2>
          </div>
          <Link
            to="/selected/archive"
            className="eyebrow border border-foreground px-5 py-2 hover:bg-foreground hover:text-background transition-colors"
          >{t("selectedPage.archiveCta")}</Link>
        </div>
      </section>

      <PhotoLightbox
        info={lightboxIndex !== null ? galleryItems[lightboxIndex]?.info ?? null : null}
        onClose={() => setLightboxIndex(null)}
        onPrev={lightboxIndex !== null && lightboxIndex > 0 ? () => setLightboxIndex((i) => (i ?? 0) - 1) : undefined}
        onNext={lightboxIndex !== null && lightboxIndex < galleryItems.length - 1 ? () => setLightboxIndex((i) => (i ?? 0) + 1) : undefined}
        neighborImages={lightboxIndex !== null ? [galleryItems[lightboxIndex - 1]?.image, galleryItems[lightboxIndex + 1]?.image] : undefined}
      />
    </div>
  );
}

function CommunityUploadSection() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [maxImages, setMaxImages] = useState<number>(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [region, setRegion] = useState("");
  const [captions, setCaptions] = useState<string[]>([]);
  const [instagram, setInstagram] = useState("");
  const [website, setWebsite] = useState("");
  const [bio, setBio] = useState("");
  const [consent, setConsent] = useState(false);
  const [igConsent, setIgConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [me, setMe] = useState<{
    userId: string;
    name: string;
    email: string;
    region: string;
    instagram: string;
    website: string;
    bio: string;
  } | null>(null);

  useEffect(() => {
    getSettingInt(SETTING_COMMUNITY_MAX_IMAGES, 1).then(setMaxImages);
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!active || !user) return;
      const cols = "user_id,name,email,region,city,instagram_handle,website_url,bio";
      const photog = await supabase.from("photographers").select(cols).eq("user_id", user.id).maybeSingle();
      let row: Record<string, string | null> | null = photog.data as Record<string, string | null> | null;
      if (!row) {
        const vis = await supabase.from("visagists").select(cols).eq("user_id", user.id).maybeSingle();
        row = vis.data as Record<string, string | null> | null;
      }
      if (!active || !row) return;
      setMe({
        userId: user.id,
        name: row.name ?? "",
        email: row.email ?? user.email ?? "",
        region: row.city ?? row.region ?? "",
        instagram: row.instagram_handle ?? "",
        website: row.website_url ?? "",
        bio: row.bio ?? "",
      });
    })();
    return () => { active = false; };
  }, []);

  function reset() {
    setFiles([]); setCategories([]); setPreviews([]); setName(""); setEmail(""); setRegion("");
    setCaptions([]); setInstagram(""); setWebsite(""); setBio("");
    setConsent(false); setIgConsent(false); setError(null); setDone(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function removeFile(index: number) {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setCategories((prev) => prev.filter((_, i) => i !== index));
    setCaptions((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => {
      const url = prev[index];
      if (url) URL.revokeObjectURL(url);
      return prev.filter((_, i) => i !== index);
    });
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (files.length === 0) { setError(t("selectedPage.errAtLeastOne")); return; }
    if (files.length > maxImages) {
      setError(maxImages === 1 ? t("selectedPage.errMaxOne", { max: maxImages }) : t("selectedPage.errMaxMany", { max: maxImages }));
      return;
    }
    if (!me && (!name.trim() || !email.trim())) { setError(t("selectedPage.errNameEmail")); return; }
    if (!consent) { setError(t("selectedPage.errConsent")); return; }
    if (!igConsent) { setError(t("selectedPage.errInstagramConsent")); return; }
    setSubmitting(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        await submitCommunityPhoto({
          file,
          submitter_name: me ? me.name : name,
          submitter_email: me ? me.email : email,
          region: me ? me.region : region,
          theme: categories[i] || "",
          caption: captions[i] ?? "",
          instagram_handle: me ? me.instagram : instagram,
          website_url: me ? me.website : website,
          bio: me ? me.bio : bio,
          user_id: me ? me.userId : null,
          instagram_consent: igConsent,
        });
      }
      setDone(true);
      reset();
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("selectedPage.errUpload"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section id="einreichen" className="border-b border-border scroll-mt-24">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 pt-12 pb-16">
        <div className="eyebrow text-muted-foreground">{t("selectedPage.heroEyebrow")}</div>
        <h1 className="serif text-5xl md:text-7xl mt-6 leading-[0.95]">
          {t("selectedPage.heroTitle1")}<br /><em className="italic">{t("selectedPage.heroTitleEm")}</em>{t("selectedPage.heroTitle2")}
        </h1>
        <p className="mt-8 max-w-xl text-muted-foreground">
          {t("selectedPage.heroText")}
        </p>

        <div className="mt-12 border-t border-border pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="max-w-xl">
            <div className="eyebrow text-muted-foreground">{t("selectedPage.submitEyebrow")}</div>
            <p className="mt-3 text-sm text-muted-foreground">
              {t("selectedPage.submitText")}
            </p>
            {me && (
              <p className="mt-2 text-sm text-foreground">
                {t("selectedPage.loggedInInfo")}
              </p>
            )}
          </div>
          {!open && !done && (
            <button
              type="button"
              onClick={() => { reset(); setOpen(true); }}
              className="eyebrow shrink-0 border border-foreground px-5 py-2 hover:bg-foreground hover:text-background transition-colors w-fit"
            >{t("selectedPage.submitBtn")}</button>
          )}
        </div>

        {(open || done) && (
          <div className="mt-10">
            {done && (
              <div className="border border-border bg-background p-6 max-w-2xl">
                <div className="serif text-xl">{t("selectedPage.thanksTitle")}</div>
                <p className="mt-2 text-sm text-muted-foreground">
                  {t("selectedPage.thanksText")}
                </p>
                <button
                  type="button"
                  onClick={() => { setDone(false); setOpen(false); }}
                  className="eyebrow mt-6 border border-foreground px-4 py-2"
                >{t("selectedPage.close")}</button>
              </div>
            )}
            {open && !done && (
              <form onSubmit={onSubmit} className="border border-border bg-background p-6 grid gap-4 max-w-2xl">
                <Field label={`${maxImages === 1 ? t("selectedPage.imageSingular") : t("selectedPage.imagePlural")} (${t("selectedPage.uploadFormat")}${maxImages > 1 ? t("selectedPage.uploadUpTo", { max: maxImages }) : ""})`}>
                  <label className="relative flex flex-col items-center justify-center gap-2 border-2 border-dashed border-foreground/40 bg-muted/20 hover:bg-muted/40 hover:border-foreground transition-colors cursor-pointer px-6 py-10 text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <span className="serif text-base">
                      {files.length > 0 ? t("selectedPage.chooseOther") : t("selectedPage.chooseHere", { word: maxImages === 1 ? t("selectedPage.imageSingular") : t("selectedPage.imagePlural") })}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {t("selectedPage.uploadClickHint")}{maxImages > 1 ? t("selectedPage.uploadUpToFiles", { max: maxImages }) : ""}
                    </span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      multiple={maxImages > 1}
                      onChange={(e) => {
                        const picked = Array.from(e.target.files ?? []);
                        setFiles((prev) => {
                          const merged = [...prev];
                          for (const f of picked) {
                            if (merged.length >= maxImages) break;
                            if (merged.some((m) => m.name === f.name && m.size === f.size && m.lastModified === f.lastModified)) continue;
                            merged.push(f);
                          }
                          const added = merged.slice(prev.length);
                          setCategories((cur) => [...cur, ...added.map(() => "open")]);
                          setPreviews((cur) => [...cur, ...added.map((f) => URL.createObjectURL(f))]);
                          setCaptions((cur) => [...cur, ...added.map(() => "")]);
                          return merged;
                        });
                        if (e.target) e.target.value = "";
                      }}
                      required={files.length === 0}
                      className="sr-only"
                    />
                  </label>
                  {files.length > 0 && (
                    <div className="text-xs text-muted-foreground mt-2">
                      {t("selectedPage.selectedCount", { count: files.length, max: maxImages, names: files.map((f) => f.name).join(", ") })}
                    </div>
                  )}
                </Field>
                {files.length > 0 && (
                  <Field label={t("selectedPage.fieldTheme")}>
                    <div className="grid gap-4">
                      {files.map((f, i) => (
                        <div key={i} className="flex items-start gap-3 border border-border bg-muted/10 p-3">
                          <div className="relative shrink-0">
                            {previews[i] ? (
                              <img
                                src={previews[i]}
                                alt={f.name}
                                className="w-16 h-16 object-cover border border-border"
                              />
                            ) : (
                              <div className="w-16 h-16 border border-border bg-muted/30 flex items-center justify-center">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground">
                                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                  <circle cx="9" cy="9" r="2" />
                                  <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
                                </svg>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0 grid gap-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground truncate">{f.name}</span>
                              <button
                                type="button"
                                onClick={() => removeFile(i)}
                                className="ml-auto shrink-0 text-muted-foreground hover:text-destructive transition-colors"
                                title={t("selectedPage.removeImage")}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                  <line x1="18" y1="6" x2="6" y2="18" />
                                  <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                              </button>
                            </div>
                            <select
                              value={categories[i] ?? "open"}
                              onChange={(e) => setCategories((cur) => cur.map((c, idx) => (idx === i ? e.target.value : c)))}
                              className="border border-input bg-background px-3 py-2 text-sm w-full"
                            >
                              {themes.map((th) => (
                                <option key={th.id} value={th.id}>{th.label}</option>
                              ))}
                            </select>
                            <input
                              type="text"
                              value={captions[i] ?? ""}
                              onChange={(e) => setCaptions((cur) => cur.map((c, idx) => (idx === i ? e.target.value : c)))}
                              maxLength={200}
                              placeholder={t("selectedPage.fieldCaption")}
                              className="w-full border border-input bg-background px-3 py-2 text-sm"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </Field>
                )}
                {me ? (
                  <div className="text-xs text-muted-foreground border border-border bg-muted/20 p-3">
                    {t("selectedPage.submittingAs", { name: me.name })}
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label={t("selectedPage.fieldNameReq")}>
                      <Input value={name} onChange={setName} required maxLength={120} />
                    </Field>
                    <Field label={t("selectedPage.fieldEmailReq")}>
                      <Input type="email" value={email} onChange={setEmail} required maxLength={255} />
                    </Field>
                    <Field label={t("selectedPage.fieldRegion")}>
                      <Input value={region} onChange={setRegion} maxLength={120} />
                    </Field>
                  </div>
                )}
                {!me && (
                  <>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label={t("selectedPage.fieldInstagram")}>
                        <Input value={instagram} onChange={setInstagram} maxLength={120} placeholder="@…" />
                      </Field>
                      <Field label={t("selectedPage.fieldWebsite")}>
                        <Input type="url" value={website} onChange={setWebsite} maxLength={300} placeholder="https://…" />
                      </Field>
                    </div>
                    <Field label={t("selectedPage.fieldBio")}>
                      <textarea
                        value={bio}
                        onChange={(e) => setBio(e.target.value)}
                        rows={3}
                        maxLength={800}
                        className="w-full border border-input bg-background px-3 py-2 text-sm"
                      />
                    </Field>
                  </>
                )}
                <label className="flex gap-3 text-xs text-muted-foreground border border-border bg-muted/20 p-3">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    required
                    className="mt-0.5 shrink-0"
                  />
                  <span>
                    {t("selectedPage.consent")}
                  </span>
                </label>
                <label className="flex gap-3 text-xs text-muted-foreground border border-border bg-muted/20 p-3">
                  <input
                    type="checkbox"
                    checked={igConsent}
                    onChange={(e) => setIgConsent(e.target.checked)}
                    required
                    className="mt-0.5 shrink-0"
                  />
                  <span>
                    {t("selectedPage.instagramConsent")}
                  </span>
                </label>
                {error && <div className="text-sm text-destructive">{error}</div>}
                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="eyebrow border border-foreground bg-foreground text-background px-5 py-2 disabled:opacity-50"
                  >{submitting ? t("selectedPage.uploadBusy") : t("selectedPage.submitBtn2")}</button>
                  <button
                    type="button"
                    onClick={() => { reset(); setOpen(false); }}
                    className="eyebrow border border-foreground px-5 py-2"
                  >{t("selectedPage.cancel")}</button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="eyebrow text-muted-foreground mb-2">{label}</div>
      {children}
    </label>
  );
}

function Input({ value, onChange, type = "text", required, maxLength, placeholder }: {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  maxLength?: number;
  placeholder?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      maxLength={maxLength}
      placeholder={placeholder}
      className="w-full border border-input bg-background px-3 py-2 text-sm"
    />
  );
}